import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { Championship, ChampionshipTournamentSummary } from 'models/championship';
import { School } from 'models/school';
import { Tournament } from 'models/tournament';
import { getChampionshipOverview, getSchoolTournament } from 'services/school';
import TeamTournamentLive from 'views/TeamTournamentLive';
import SchoolEventView from 'views/School/SchoolEventView';
import Loader from 'components/loader';
import { getAccessibleBrandColor, mixBrandColor, resolveSchoolPublicSite } from 'helpers/publicSite';
import './ChampionshipLive.scss';
import './ChampionshipBranding.scss';
import './ChampionshipProgress.scss';
import SponsorQR,{sponsorUrl} from 'components/SponsorQR';
import './ChampionshipSponsors.scss';

interface ShellProps { championship: Championship; school: School; }
interface TournamentProps extends ShellProps {}

const shortDate = (value?: string) => value
   ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
   : '';
const initials = (value: string) => value.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
const tournamentIdOf = (item: any) => String(item?.id || item?._id || '');
const isV2TeamTournament = (item: ChampionshipTournamentSummary) =>
   item.type === 'TEAM_TOURNAMENT' && item.publicSite?.templateVersion === 'TEAM_TOURNAMENT_V2';

export function ChampionshipLiveLayout({ championship, school }: ShellProps) {
   const tournaments = championship.tournaments || [];
   const schoolSite = resolveSchoolPublicSite(school);
   const brand = getAccessibleBrandColor(schoolSite.appearance.brandColor);
   const heroImages = (championship.photos || []).map(photo => photo.picUrl).filter(Boolean);
   if (!heroImages.length && schoolSite.appearance.headerImageUrl) heroImages.push(schoolSite.appearance.headerImageUrl);
   const [heroIndex, setHeroIndex] = useState(0);
   useEffect(() => {
      if (heroImages.length < 2) return undefined;
      const timer = window.setInterval(() => setHeroIndex(index => (index + 1) % heroImages.length), 7000);
      return () => window.clearInterval(timer);
   }, [heroImages.join('|')]);
   const hero = heroImages[heroIndex];
   const cssVariables = {
      '--sc-brand': brand,
      '--sc-brand-soft': mixBrandColor(brand, '#ffffff', 0.88),
      '--sc-brand-muted': mixBrandColor(brand, '#ffffff', 0.68),
      '--sc-brand-deep': mixBrandColor(brand, '#000000', 0.18)
   } as React.CSSProperties;
   return <div className="chlSite" style={cssVariables}>
      <header className="chlHeader">
         <Link className="chlBrand" to="/">
            <span>{school.pic ? <img src={school.pic} alt="" /> : initials(school.name || 'SIT')}</span>
            <div><small>{school.name}</small><strong>{championship.name}</strong></div>
         </Link>
         <nav>
            <Link to="/">Overview</Link>
            <a href="#championship-tournaments">Tournaments</a>
         </nav>
         <div className="chlLive"><i />Live updates</div>
      </header>
      <div className={`chlHero ${hero ? 'has-image' : ''}`} style={hero ? { backgroundImage: `linear-gradient(90deg,rgba(10,31,49,.9),rgba(10,31,49,.28)),url(${hero})` } : undefined}>
         <span>Championship</span><h1>{championship.name}</h1>
         <p>{shortDate(championship.startTime)}{championship.endTime ? ` – ${shortDate(championship.endTime)}` : ''} · {tournaments.length} tournament{tournaments.length === 1 ? '' : 's'}</p>
         {heroImages.length > 1 && <nav className="chlHero__dots" aria-label="Championship photos">{heroImages.map((image, index) => <button type="button" className={index === heroIndex ? 'is-active' : ''} aria-label={`Show championship photo ${index + 1}`} aria-pressed={index === heroIndex} onClick={() => setHeroIndex(index)} key={`${image}-${index}`} />)}</nav>}
      </div>
      <Outlet context={{ championship, school }} />
      <footer className="chlFooter"><span>Powered by Squad In Touch</span><span>{championship.organisersEmail}</span></footer>
   </div>;
}

export function ChampionshipOverview({ championship }: { championship: Championship; school: School }) {
   const tournaments = championship.tournaments || [];
   const [liveOverview, setLiveOverview] = useState<any>();
   // The organiser controls this order in Championship management. Do not
   // silently replace it with chronological order on the public website.
   const ordered = useMemo(() => [...tournaments], [tournaments]);
   const now = Date.now();
   const upcoming = ordered.filter(item => +new Date(item.startTime) > now);
   useEffect(() => {
      let mounted = true;
      const refresh = () => getChampionshipOverview(championship.id).then(value => { if (mounted) setLiveOverview(value); }).catch(() => undefined);
      refresh();
      const timer = window.setInterval(refresh, 15000);
      return () => { mounted = false; window.clearInterval(timer); };
   }, [championship.id]);
   const statusFor = (id: string) => liveOverview?.tournaments?.find((item: any) => item.tournamentId === id);
   const liveCount = liveOverview?.totals?.live ?? 0;
   return <main className="chlMain">
      <section className="chlIntro">
         <div><span>Championship overview</span><h2>{liveCount ? `${liveCount} live match${liveCount === 1 ? '' : 'es'} now` : upcoming.length ? 'The next tournaments are ready' : 'Championship programme'}</h2><p>Select a tournament for its live matches, groups, knockout route, fixtures and schools.</p></div>
         <dl><div><dt>{tournaments.length}</dt><dd>Tournaments</dd></div><div><dt>{liveCount}</dt><dd>Live matches</dd></div><div><dt>{liveOverview?.totals?.completed ?? 0}</dt><dd>Finished</dd></div></dl>
      </section>
      <section className="chlTournamentSection" id="championship-tournaments"><header><div><span>Programme</span><h2>Championship tournaments</h2></div></header>
         {ordered.length ? <div className="chlTournamentGrid">{ordered.map(item => <TournamentCard item={item} status={statusFor(tournamentIdOf(item))} key={tournamentIdOf(item)} />)}</div> : <div className="chlEmpty"><strong>No tournaments have been published yet</strong><span>Please check back when the programme is ready.</span></div>}
      </section>
      {Boolean(championship.sponsors?.length)&&<section className="chlSponsors"><header><span>With thanks to</span><h2>Championship sponsors</h2></header><div>{championship.sponsors!.map((sponsor,index)=>{const href=sponsorUrl(sponsor.link);const body=<>{sponsor.picUrl?<img src={sponsor.picUrl} alt=""/>:<i>{initials(sponsor.name||'Sponsor')}</i>}<strong>{sponsor.name||'Championship sponsor'}</strong>{href&&<SponsorQR value={href}/>}</>;return href?<a href={href} target="_blank" rel="noopener noreferrer" key={`${sponsor.name}-${index}`}>{body}</a>:<article key={`${sponsor.name}-${index}`}>{body}</article>;})}</div></section>}
      {championship.disclaimer && <section className="chlDisclaimer"><h3>Championship information</h3><p>{championship.disclaimer}</p></section>}
   </main>;
}

function TournamentCard({ item, status }: { item: ChampionshipTournamentSummary; status?: any }) {
   const image = item.photos?.[0]?.picUrl;
   const content = <><span className="chlTournamentCard__image" style={image ? { backgroundImage: `url(${image})` } : undefined}><i className={status?.live ? 'is-live' : ''}>{status?.live ? `Live · ${status.live} ${status.live === 1 ? 'match' : 'matches'}` : item.type === 'TEAM_TOURNAMENT' ? 'Team tournament' : 'Tournament'}</i></span><span className="chlTournamentCard__body"><small>{shortDate(item.startTime)}{item.endTime ? ` – ${shortDate(item.endTime)}` : ''}</small><strong>{item.name}</strong>{status?.matches ? <span className="chlTournamentCard__progress"><i><b style={{ width: `${Math.round(status.completed / status.matches * 100)}%` }} /></i><small>{status.completed} of {status.matches} matches finished</small></span> : null}<em>{isV2TeamTournament(item) ? 'Open live tournament' : 'Open tournament website'} →</em></span></>;
   return isV2TeamTournament(item)
      ? <Link className="chlTournamentCard" to={`/tournaments/${tournamentIdOf(item)}`}>{content}</Link>
      : <a className="chlTournamentCard" href={item.domain ? `//${item.domain}.${window.location.host.split('.').slice(1).join('.')}` : '#'}>{content}</a>;
}

export function ChampionshipTournament({ championship, school }: TournamentProps) {
   const { tournamentId } = useParams();
   const summary = (championship.tournaments || []).find(item => tournamentIdOf(item) === tournamentId);
   const [tournament, setTournament] = useState<Tournament>();
   const [error, setError] = useState('');
   useEffect(() => {
      setTournament(undefined); setError('');
      if (!summary || !tournamentId) { setError('This tournament is not part of the championship.'); return; }
      getSchoolTournament(championship.schoolId, tournamentId).then(setTournament).catch(() => setError('The tournament could not be loaded.'));
   }, [championship.schoolId, tournamentId, summary?.id]);
   if (error) return <main className="chlMain"><div className="chlEmpty"><strong>{error}</strong><Link to="/">Back to championship</Link></div></main>;
   if (!tournament) return <div className="chlLoader"><Loader /></div>;
   return <div className="chlTournamentView"><div className="chlTournamentSwitch"><Link to="/">← Championship overview</Link><span>{tournament.name}</span></div><TeamTournamentLive school={school} tournament={tournament} embedded eventPathPrefix={`/tournaments/${tournament.id}`} /></div>;
}

export function ChampionshipTournamentEvent({ championship, school }: TournamentProps) {
   const { tournamentId } = useParams();
   const included = (championship.tournaments || []).some(item => tournamentIdOf(item) === tournamentId);
   const [tournament, setTournament] = useState<Tournament>();
   const [error, setError] = useState('');
   useEffect(() => {
      setTournament(undefined); setError('');
      if (!included || !tournamentId) { setError('This tournament is not part of the championship.'); return; }
      getSchoolTournament(championship.schoolId, tournamentId).then(setTournament).catch(() => setError('The tournament could not be loaded.'));
   }, [championship.schoolId, included, tournamentId]);
   if (error) return <main className="chlMain"><div className="chlEmpty"><strong>{error}</strong><Link to="/">Back to championship</Link></div></main>;
   if (!tournament) return <div className="chlLoader"><Loader /></div>;
   return <div className="chlTournamentView"><div className="chlTournamentSwitch"><Link to={`/tournaments/${tournament.id}`}>← Back to {tournament.name}</Link><span>{championship.name}</span></div><SchoolEventView school={school} /></div>;
}
